import isNumber from 'lodash/isNumber';
import {useEffect} from 'react';
import {useDispatch} from 'react-redux';
import {InfinityScroll} from '../../design-system/atoms/InfinityScroll/InfinityScroll';
import {Loading} from '../../design-system/atoms/Loading/Loading';
import {
  StaticLandingFreeSeriesSection,
  StaticLandingFreeSeriesSectionQueryVariables,
} from '../../graphql/generated/types';
import {onSuccessStaticLandingSection} from '../../pagewebLayout/PagewebLayoutSlice';
import {CACHE_ONLY_CONFIG} from '../../reactQuery/constant';
import {useInfiniteStaticLandingFreeSeriesSectionQuery} from '../../wrapper/landingWrapper';
import useTypedSelector from '../useTypedSelector';
import {StaticLandingFreeSeriesMoreFetcherProps} from './StaticLandingFreeSeriesMoreFetcherProps.interface';

export function StaticLandingFreeSeriesMoreFetcher({
  children,
  section,
}: StaticLandingFreeSeriesMoreFetcherProps) {
  const staticLandingFreeSeriesSection = useTypedSelector(
    state =>
      state.pagewebLayout.entities.sections[section.id] as Omit<
        StaticLandingFreeSeriesSection,
        'groups'
      >
  );

  const {param: currentParam} = staticLandingFreeSeriesSection;
  const param: StaticLandingFreeSeriesSectionQueryVariables['param'] = {
    categoryUid: currentParam.categoryUid,
    // 카테고리가 변경되는 경우에는 tab이 직접 명시된 경우가 아니라면 현재 선택된 탭 정보가 초기화돼야 합니다.
    tabUid: currentParam.tab?.param,
    screenUid: isNumber(currentParam.screenUid)
      ? currentParam.screenUid
      : undefined,
  };
  const {data, isSuccess, fetchNextPage, isFetching, isFetchedAfterMount} =
    useInfiniteStaticLandingFreeSeriesSectionQuery(
      {
        sectionId: section.id,
        param,
      },
      {
        initialPageParam: {sectionId: section.id, param},
        getNextPageParam: lastPage => {
          const section =
            lastPage.staticLandingFreeSeriesSection as StaticLandingFreeSeriesSection;
          return section.isEnd
            ? undefined
            : {
                param: {
                  ...param,
                  page: (section.param.page || 0) + 1,
                },
                sectionId: section.id,
              };
        },
        initialData: staticLandingFreeSeriesSection
          ? {
              pages: [
                {
                  staticLandingFreeSeriesSection:
                    staticLandingFreeSeriesSection as StaticLandingFreeSeriesSection,
                },
              ],
              pageParams: [{sectionId: section.id, param}],
            }
          : undefined,
        ...CACHE_ONLY_CONFIG,
      }
    );

  const dispatch = useDispatch();

  const lastPage = data?.pages[data.pages.length - 1]
    ?.staticLandingFreeSeriesSection as
    | StaticLandingFreeSeriesSection
    | undefined;
  useEffect(() => {
    if (isFetchedAfterMount && isSuccess) {
      dispatch(
        onSuccessStaticLandingSection(lastPage, {
          sectionId: section.id,
          page: lastPage?.param.page || 0,
        })
      );
    }
  }, [dispatch, isFetchedAfterMount, isSuccess, lastPage, section.id]);

  return (
    <>
      <InfinityScroll
        isLoading={isFetching}
        canLoadNextPage={!isFetching && !lastPage?.isEnd}
        requestNextPageData={fetchNextPage}
      >
        {children}
      </InfinityScroll>
      {isFetching && (
        <div className="flex-center-full">
          <Loading />
        </div>
      )}
    </>
  );
}
